import { useCallback } from 'react';
import { action } from 'mobx';
import { IDetailsFormData } from '../types/IDetailsFormData';
import { useNewBookingStore } from '../../../../domain/booking/stores/useNewBookingsStore';
import { useNavigate } from '../../../../../shared/router/useNavigate';
import { useVehicleStores } from '../../../../../account/vehicles/domain/stores/useVehicleStores';
import { NewVehicle } from '../../../../../shared/api/gateway/types/NewVehicle';
import { useTranslation } from '../../../../../shared/i18n/translate/useTranslation';
import { trackAddVehicle } from '../../../../../account/vehicles/shared/analytics';
import { EVENT_LABEL_VEHICLES_PARKING_NEW } from '../../../../../shared/analytics';
import { useAuthStore } from '../../../../../auth/shared/store/useAuthStore';

export function useHandleSubmitNewBooking(nextUrl: string) {
	const { t } = useTranslation();
	const store = useNewBookingStore();
	const { getVehicleStore, addVehicleStore } = useVehicleStores();
	const authStore = useAuthStore();

	const navigate = useNavigate();

	const handleSubmit = useCallback(
		action((data: IDetailsFormData) => {
			const {
				licensePlateNumber,
				saveVehicle,
				reasonForTravel,
				shouldCreateNewUserAccount,
				usedExistingVehicleFromAccount,
				...personalDetails
			} = data;

			store.booking.personalDetails = personalDetails;
			store.booking.emailOptIn = authStore.isAuthenticated
				? true
				: Boolean(shouldCreateNewUserAccount);
			store.booking.vehicleDetails = {
				licensePlateNumber,
				saveVehicle,
				usedExistingVehicleFromAccount,
			};
			store.booking.travelDetails = { reasonForTravel };

			// If user needs the vehicle saved for next time, we add it to the store here
			if (saveVehicle) {
				const currentVehicles = getVehicleStore.value || [];
				const isFavorite = currentVehicles.length === 0;

				const newVehicle: NewVehicle = {
					description: `${t('add_vehicle_empty_description')} ${
						currentVehicles.length + 1
					}`,
					licensePlate: licensePlateNumber,
					favorite: isFavorite,
				};

				addVehicleStore.load(newVehicle);
				trackAddVehicle(EVENT_LABEL_VEHICLES_PARKING_NEW);
			}

			navigate(nextUrl);
		}),
		[],
	);

	return { handleSubmit };
}
